import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useAuth } from './AuthProvider'
import { Pentacle } from '../design/components'
import styles from './RequireAuth.module.css'

/**
 * Gates a route on an authenticated session.
 *
 * The `loading` state is not cosmetic: on a page reload the access token is gone
 * (it lives in memory) while the refresh token is not, so there is a real moment
 * where the answer is "not yet known". Redirecting during it would bounce a
 * signed-in user to the login screen on every refresh.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return (
      <div className={styles.wait} role="status" aria-live="polite">
        <Pentacle size={40} className={styles.sigil} />
        <span className={styles.text}>Restoring your session…</span>
      </div>
    )
  }

  if (status === 'anonymous') {
    // `from` is carried so login can return the user where they were headed.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}
